/**
 * AddAllergyEditor — inline Add/Edit form for the Allergies chart tab
 * (PROD-827 chart write surfaces).
 *
 * Flutter ref: `patient_chart/.../tab_widgets/allergies_tab.dart` (inline
 * editor at L143-708). Flutter renders the form INLINE inside the chart
 * card with the existing list pushed below — this matches that pattern
 * 1:1 in React.
 *
 * **Two modes, same form:**
 * - Add — `editing` prop omitted. Submit calls
 *   `useCreateAllergy` with `CreateAllergyBody`. The allergen's
 *   category ships at TOP LEVEL and `code` is a plain Coding — the
 *   backend ignores a category nested under `code` (PROD-1998).
 * - Edit — `editing` prop is the existing `PatientAllergy`. Submit
 *   calls `useUpdateAllergy` with `UpdateAllergyBody` (curl-verified
 *   2026-05-19). Round-trip rules:
 *   - `identifier`, `recordedDate`, `asserter`, `createdDate`,
 *     `performerDetails` come from the original record.
 *   - **`recorder` is the ORIGINAL recording user**, not the current
 *     editor (curl-locked).
 *   - As on CREATE, the category Coding goes top-level (NOT nested
 *     under code). The form's allergen picker returns the nested
 *     shape; we extract on submit.
 *
 * Slice scope:
 *  - **Drug / Non-drug toggle** (Flutter `allergies_tab.dart:275-296`,
 *    PROD-1998). Non-drug searches `/api/Master/AllergyCode`; Drug
 *    searches the org's medication catalogue
 *    (`SearchValueSets2?SearchFor=Medications`) and carries
 *    `DRUG_ALLERGY_CATEGORY`. Switching clears the picked allergen; on
 *    edit the toggle is seeded from the record's category.
 *  - **Delete** is handled in the parent (`AllergiesChartTab`) as a
 *    confirm-dialog flow that calls `useUpdateAllergy` with
 *    `isDeleted: true`. Same endpoint, soft-delete semantics.
 */

import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Search } from 'lucide-react';
import { toast } from 'sonner';

import { Button, FieldLabel, Segmented, Select } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { TextArea as Textarea } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAuthStore } from '@/auth/auth-store';
import { useAllergenReactions } from '@/patients/services/use-allergen-reactions';
import { useAllergenSeverities } from '@/patients/services/use-allergen-severities';
import { useAllergyCodeSearch } from '@/patients/services/use-allergy-code-search';
import { useMedicationValueSetSearch } from '@/encounter/services/use-medication-valueset-search';
import {
  allergenKindOf,
  codingOptionKey,
  findCodingByOptionKey,
  medicationToAllergen,
  resolveCodingOptionKey,
  type AllergenKind,
} from '@/encounter/lib/to-allergy-request';
import { useCreateAllergy } from '@/patients/services/use-create-allergy';
import { useUpdateAllergy } from '@/patients/services/use-update-allergy';
import {
  readStringField,
  unwrapMasterRow,
} from '@/patients/lib/unwrap-master-row';
import { DynamicDateInput } from './dynamic-date-input';
import type {
  AllergyCodeOption,
  CreateAllergyBody,
  PatientAllergy,
  PerformerDetailsFull,
  UpdateAllergyBody,
} from '@/types/patient-chart-api';
import type { CodeWordCase } from '@/types/patient-api';
import { V2_BODY_EMPHASIS, v2Look } from '@/shared/ui/design-v2';

// ──────────────────────────────────────────────────────────────────────────
// Zod schema
// ──────────────────────────────────────────────────────────────────────────

/**
 * Form fields. Allergen is the JSON-encoded picker selection (custom
 * chip render — never goes through shadcn's SelectValue, so the long
 * string is fine). Reaction + Severity use SCALAR keys
 * (`codingOptionKey` = `system|code|display`) so the Select trigger
 * renders the display cleanly. The key is NOT the bare `code`: the
 * master lists carry duplicate codes (PROD-2735), and a code key saved
 * whichever duplicate came first. Full Codings are reconstructed at
 * submit time by looking up the key against the master-data response.
 */
const schema = z
  .object({
    /** Selected allergen — stringified `AllergyCodeOption`. */
    allergenJson: z.string().min(1, 'Pick an allergen'),
    /** Selected reaction option key — `codingOptionKey` of the master row. */
    reactionKey: z.string().min(1, 'Reaction is required'),
    /** Selected severity option key — `codingOptionKey` of the master row. */
    severityKey: z.string().min(1, 'Severity is required'),
    /** User's free-text input — preserved verbatim and shipped to the
     *  backend as `onSetDisplay`. */
    onsetDisplay: z
      .string()
      .min(1, 'Start date is required')
      .max(50),
    /** ISO resolved by `DynamicDateInput`'s parser. Null when the
     *  free-text didn't parse — the refine below blocks submit. */
    onsetIso: z.string().nullable(),
    note: z.string().max(500).optional(),
  })
  // Defensive guard: backend's `onset` field has no curl evidence that
  // it accepts null. Flutter resolves partial dates via DynamicDatePicker
  // internally, never null. Block submit if the input didn't resolve
  // (Samer PR #214 review 2026-05-19, same pattern as Problems).
  .refine((v) => v.onsetIso !== null, {
    message:
      'Start date must be a parseable date (DD/MM/YYYY, MM/YYYY, YYYY, or 1Y/2M/3D)',
    path: ['onsetDisplay'],
  });

type FormValues = z.infer<typeof schema>;

// ──────────────────────────────────────────────────────────────────────────
// Inline editor
// ──────────────────────────────────────────────────────────────────────────

export interface AddAllergyEditorProps {
  patientId: string;
  /** Called when the user saves OR cancels — parent collapses the editor. */
  onClose: () => void;
  /** Existing allergy record to edit. When present, the form is
   *  pre-populated and submit goes to `PUT /allergy/{id}`. When absent,
   *  this is the Add flow and submit goes to `POST /CreateAllergy`. */
  editing?: PatientAllergy | null;
}

export function AddAllergyEditor({
  patientId,
  onClose,
  editing = null,
}: AddAllergyEditorProps) {
  const isEditing = !!editing?.identifier;

  // Defensive: `user` may be null (logged out mid-session) OR `userId`
  // may itself be missing on the user object (data corruption). The
  // submit handler treats either as a no-go because `recorder` is a
  // required FHIR-side ref and the backend rejects empty GUIDs with
  // a 500 (Guid.Parse). Samer PR #214 review 2026-05-19.
  const user = useAuthStore((s) => s.user);
  const userId = user?.userId ?? '';

  const reactionsQ = useAllergenReactions(true);
  const severitiesQ = useAllergenSeverities(true);
  // Drug / Non-drug — seeded from the record's category on edit (PROD-1998).
  const [kind, setKind] = useState<AllergenKind>(() => allergenKindOf(editing?.category));

  // Pre-populate the form from `editing` when present. The Allergen
  // picker takes an `AllergyCodeOption` shape (code/display/system +
  // optional nested category). On Edit, the existing record carries
  // category at TOP-LEVEL — we re-nest it back into the picker's
  // option shape so the chip renders the right category subtitle.
  const initialValues = useMemo<FormValues>(() => {
    if (!editing) {
      return {
        allergenJson: '',
        reactionKey: '',
        severityKey: '',
        onsetDisplay: '',
        onsetIso: null,
        note: '',
      };
    }
    const allergenOpt: AllergyCodeOption | null = editing.code
      ? {
          code: editing.code.code ?? null,
          display: editing.code.display ?? null,
          system: editing.code.system ?? null,
          description: editing.code.description ?? null,
          shortName: editing.code.shortName ?? null,
          category: editing.category ?? null,
        }
      : null;
    return {
      allergenJson: allergenOpt ? JSON.stringify(allergenOpt) : '',
      // Keys built from the STORED Codings; resolveCodingOptionKey maps them
      // onto the master row (code+display first, then code) once it loads.
      reactionKey: editing.reaction?.substance
        ? codingOptionKey(editing.reaction.substance)
        : '',
      severityKey: editing.criticality ? codingOptionKey(editing.criticality) : '',
      onsetDisplay: editing.onSetDisplay ?? '',
      onsetIso: editing.onset ?? null,
      note: editing.note ?? '',
    };
  }, [editing]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues,
  });

  const createMut = useCreateAllergy();
  const updateMut = useUpdateAllergy();
  const isPending = createMut.isPending || updateMut.isPending;

  const submit = form.handleSubmit(async (values) => {
    if (!user) {
      toast.error('Your session has expired. Please sign in again.');
      return;
    }
    if (!userId) {
      // User object exists but `userId` is missing — auth-store
      // corruption. Surfaced as a distinct message so support can tell
      // the two cases apart from a screenshot.
      toast.error("Couldn't identify the recording user (missing userId).");
      return;
    }
    const allergen = safeParse<AllergyCodeOption>(values.allergenJson);
    const reaction = findCodingByOptionKey(
      reactionsQ.data,
      resolveCodingOptionKey(reactionsQ.data, values.reactionKey),
    );
    const severity = findCodingByOptionKey(
      severitiesQ.data,
      resolveCodingOptionKey(severitiesQ.data, values.severityKey),
    );

    if (!allergen || !reaction || !severity) {
      toast.error('Form values are corrupted — please re-pick allergen, reaction, and severity.');
      return;
    }

    // Onset ISO already resolved by `DynamicDateInput`'s parser and
    // stored in form state. Refine guard above guarantees it's
    // non-null when we reach this point.
    const resolvedOnset = values.onsetIso;

    try {
      if (isEditing && editing) {
        // STRICT recorder preservation — block the edit if the original
        // record didn't carry a recorder GUID rather than silently
        // substituting the current user (Samer PR #219 review). The
        // backend's `recorder` field is FHIR-side ref and meaningful
        // for audit; clobbering it would corrupt the chart history.
        if (!editing.recorder) {
          toast.error(
            "Cannot edit — this record is missing its original recorder. Please contact support.",
          );
          return;
        }
        // UPDATE wire: category at top-level, code is plain CodeWordCase
        // (no nested category). `code` is built field-by-field — NOT
        // spread from `allergen` — so the picker's nested `category`
        // field can't leak into the UPDATE body even by accident (Samer
        // PR #219 review §3).
        const body: UpdateAllergyBody = {
          identifier: editing.identifier!,
          clinicalStatus: 'Active',
          type: 'allergy',
          // Normalize every Coding to 5-field present-with-null shape —
          // master-data + GET responses can ship 3-field Codings, and
          // JSON.stringify would drop the missing fields rather than
          // shipping explicit nulls. Flutter wire parity is 5-field.
          category: normalizeCoding(allergen.category),
          criticality: normalizeCoding(severity),
          code: {
            code: allergen.code ?? null,
            display: allergen.display ?? null,
            system: allergen.system ?? null,
            description: allergen.description ?? null,
            shortName: allergen.shortName ?? null,
          },
          subject: patientId,
          dataAbsentReason: 'n/a',
          encounter: editing.encounter ?? null,
          onset: resolvedOnset,
          onSetDisplay: values.onsetDisplay,
          recordedDate: editing.recordedDate ?? null,
          recorder: editing.recorder,
          asserter: editing.asserter ?? editing.recorder,
          note: values.note?.trim() || '',
          reaction: {
            substance: normalizeCoding(reaction),
            onset: 'n/a',
            severity: 'n/a',
          },
          isDeleted: false,
          performerDetails: normalizePerformerDetails(editing.performerDetails),
          createdDate: editing.createdDate ?? null,
          // Flutter parity: top-level speciality ships `null` on UPDATE
          // even when the existing record carries one. The provider's
          // speciality is already nested under `performerDetails.speciality`
          // and that's where the backend reads it from.
          speciality: null,
        };
        await updateMut.mutateAsync({
          patientId,
          allergyId: editing.identifier!,
          body,
        });
        toast.success(`Updated allergy: ${allergen.display ?? 'Unknown'}`);
      } else {
        // CREATE wire: category at TOP LEVEL (the only place the backend
        // reads it), code a plain Coding (PROD-1998). Drug -> DRUG_ALLERGY_CATEGORY
        // (stamped by medicationToAllergen); non-drug -> the row's own category,
        // null when it has none.
        const body: CreateAllergyBody = {
          identifier: null,
          clinicalStatus: 'Active',
          type: 'allergy',
          category: normalizeCoding(allergen.category),
          criticality: severity,
          code: {
            code: allergen.code ?? null,
            display: allergen.display ?? null,
            system: allergen.system ?? null,
            description: allergen.description ?? null,
            shortName: allergen.shortName ?? null,
          },
          subject: patientId,
          dataAbsentReason: 'n/a',
          encounter: null,
          onset: resolvedOnset,
          onSetDisplay: values.onsetDisplay,
          recordedDate: null,
          recorder: userId,
          asserter: null,
          note: values.note?.trim() || null,
          reaction: {
            substance: reaction,
            onset: 'n/a',
            severity: 'n/a',
          },
          isDeleted: false,
          performerDetails: null,
          createdDate: null,
          speciality: null,
        };
        await createMut.mutateAsync({ patientId, body });
        toast.success(`Added allergy: ${allergen.display ?? 'Unknown'}`);
      }
      form.reset();
      onClose();
    } catch {
      /* Global error toast handles the surface — keep the editor open
       * so the user can retry without losing their input. */
    }
  });

  return (
    // The design's PaEditorCard: the one card (hairline, 12 corners, white, no shadow, 24 padding); look in
    // showcase-patient.css `editor card`.
    <div data-slot="editor-card">
      <form onSubmit={submit} className="space-y-6">
        {/* Allergen typeahead — full width per Flutter Add Allergy form
         *  layout (allergen on its own row above the Reaction/Severity
         *  pair so the typeahead chip has horizontal room to breathe). */}
        <Controller
          name="allergenJson"
          control={form.control}
          render={({ field, fieldState }) => (
            <AllergenPicker
              kind={kind}
              onKindChange={(next) => {
                setKind(next);
                field.onChange('');
              }}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Reaction dropdown — scalar key prevents the JSON-leak that
           *  shadcn SelectValue exhibits with long stringified Codings.
           *  Inline error+retry UI when the master-data fetch fails so
           *  the user sees a path forward instead of an empty dropdown
           *  (Samer PR #214 review 2026-05-19). */}
          <Controller
            name="reactionKey"
            control={form.control}
            render={({ field, fieldState }) => (
              <div>
                <FieldLabel htmlFor="reaction" >
                  Reaction <span className="text-red-700">*</span>
                </FieldLabel>
                <Select
                  id="reaction"
                  className="mt-1"
                  value={resolveCodingOptionKey(reactionsQ.data, field.value) || undefined}
                  onChange={field.onChange}
                  disabled={reactionsQ.isLoading || reactionsQ.isError}
                  placeholder={
                    reactionsQ.isLoading
                      ? 'Loading…'
                      : reactionsQ.isError
                        ? 'Failed to load reactions'
                        : 'Select reaction'
                  }
                  options={(reactionsQ.data ?? []).map((r) => ({
                    label: r.display ?? r.code ?? '—',
                    value: codingOptionKey(r),
                  }))}
                />
                {reactionsQ.isError ? (
                  <MasterDataError onRetry={() => reactionsQ.refetch()} />
                ) : fieldState.error ? (
                  <p className="text-xs text-red-700 mt-1" role="alert">
                    {fieldState.error.message}
                  </p>
                ) : null}
              </div>
            )}
          />

          {/* Severity dropdown — "Severity" UI label binds to wire `criticality` */}
          <Controller
            name="severityKey"
            control={form.control}
            render={({ field, fieldState }) => (
              <div>
                <FieldLabel htmlFor="severity" >
                  Severity <span className="text-red-700">*</span>
                </FieldLabel>
                <Select
                  id="severity"
                  className="mt-1"
                  value={resolveCodingOptionKey(severitiesQ.data, field.value) || undefined}
                  onChange={field.onChange}
                  disabled={severitiesQ.isLoading || severitiesQ.isError}
                  placeholder={
                    severitiesQ.isLoading
                      ? 'Loading…'
                      : severitiesQ.isError
                        ? 'Failed to load severities'
                        : 'Select severity'
                  }
                  options={(severitiesQ.data ?? []).map((s) => ({
                    label: s.display ?? s.code ?? '—',
                    value: codingOptionKey(s),
                  }))}
                />
                {severitiesQ.isError ? (
                  <MasterDataError onRetry={() => severitiesQ.refetch()} />
                ) : fieldState.error ? (
                  <p className="text-xs text-red-700 mt-1" role="alert">
                    {fieldState.error.message}
                  </p>
                ) : null}
              </div>
            )}
          />
        </div>

        {/* Start date — single DynamicDateInput control (Flutter parity).
         *  Was a dual date-picker + free-text in the initial Slice;
         *  migrated 2026-05-20 to match the Problems editor pattern
         *  (Samer PR #225 follow-up). */}
        <Controller
          name="onsetIso"
          control={form.control}
          render={({ field: isoField }) => (
            <Controller
              name="onsetDisplay"
              control={form.control}
              render={({ field: displayField, fieldState }) => (
                <DynamicDateInput
                  label="Start date"
                  required
                  displayValue={displayField.value}
                  isoValue={isoField.value}
                  onChange={(next) => {
                    displayField.onChange(next.displayValue);
                    isoField.onChange(next.isoValue);
                  }}
                  error={fieldState.error?.message}
                />
              )}
            />
          )}
        />

        {/* Description */}
        <div>
          <FieldLabel htmlFor="note" >
            Description
          </FieldLabel>
          <Textarea
            id="note"
            {...form.register('note')}
            placeholder="Optional context, e.g. anaphylaxis history, related labs…"
            rows={3}
            className="mt-1"
          />
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button type="submit" disabled={isPending}>
            {isPending ? (
              <>
                Saving…
              </>
            ) : isEditing ? (
              'Update'
            ) : (
              'Done'
            )}
          </Button>
          <Button
            type="button"
            variant="tertiary"
            onClick={() => {
              form.reset();
              onClose();
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// AllergenPicker — typeahead search bound to /api/Master/AllergyCode
// ──────────────────────────────────────────────────────────────────────────

function AllergenPicker({
  kind,
  onKindChange,
  value,
  onChange,
  error,
}: {
  kind: AllergenKind;
  /** Toggle changed — the parent clears the picked allergen. */
  onKindChange: (next: AllergenKind) => void;
  value: string;
  onChange: (next: string) => void;
  error?: string;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 250);
  // Only the active source fires (each hook is also gated on >= 2 chars).
  const allergyQ = useAllergyCodeSearch(debounced, kind === 'non-drug');
  const drugQ = useMedicationValueSetSearch(debounced, kind === 'drug');
  const isFetching = kind === 'drug' ? drugQ.isFetching : allergyQ.isFetching;

  const selected = useMemo<AllergyCodeOption | null>(
    () => (value ? safeParse<AllergyCodeOption>(value) : null),
    [value],
  );

  // Drug mode: medications become allergen rows carrying DRUG_ALLERGY_CATEGORY,
  // so both modes render and pick through the same row shape.
  const results: AllergyCodeOption[] =
    kind === 'drug'
      ? (drugQ.data ?? []).map(medicationToAllergen)
      : (allergyQ.data ?? []);

  return (
    <div>
      {/* Drug / Non-drug — Flutter allergies_tab.dart:275-296. Switching clears the pick. */}
      <div className="mb-3">
        <Segmented<AllergenKind>
          aria-label="Allergy type"
          value={kind}
          onChange={(next) => {
            if (next === kind) return;
            setQuery('');
            onKindChange(next);
          }}
          options={[
            { value: 'non-drug', label: 'Non-drug' },
            { value: 'drug', label: 'Drug' },
          ]}
        />
      </div>
      <FieldLabel htmlFor="allergen" >
        Allergen <span className="text-red-700">*</span>
      </FieldLabel>

      {/* Selected chip + clear */}
      {selected ? (
        (() => {
          // Defensive re-unwrap — same pattern as Procedures/Family
          // History pickers. Protects against legacy stored wrapped
          // values if `/api/Master/AllergyCode` ever surfaces the
          // wrapped shape (PR #225/#232/#253 lineage).
          const inner = unwrapMasterRow(selected);
          const codeStr = readStringField(inner, 'code') ?? '';
          const displayStr = (readStringField(inner, 'display') ?? '').trim();
          const categoryDisplay = selected.category?.display ?? null;
          return (
            <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-border-default bg-blue-100/30 px-3 py-2">
              <div className="min-w-0">
                <p className={v2Look('text-sm font-medium text-brand-depth truncate', V2_BODY_EMPHASIS)}>
                  {displayStr || codeStr || '—'}
                </p>
                {categoryDisplay ? (
                  <p className="text-xs text-text-hint">{categoryDisplay}</p>
                ) : null}
              </div>
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={() => {
                  onChange('');
                  setQuery('');
                }}
              >
                Change
              </Button>
            </div>
          );
        })()
      ) : (
        <>
          <div className="relative mt-1">
            <Search
              aria-hidden
              className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-hint"
            />
            <TextField
              id="allergen"
              type="text"
              placeholder={
                kind === 'drug'
                  ? 'Type to search medications'
                  : 'Type to search allergens (e.g. peanuts, latex)'
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              inputClassName="pl-10"
              autoComplete="off"
            />
          </div>

          {debounced.trim().length >= 2 ? (
            <div className="mt-1 max-h-48 overflow-y-auto scrollbar-hide rounded-md border border-border-default bg-surface-card divide-y divide-border-default">
              {isFetching ? (
                <div className="px-3 py-2 text-sm text-text-muted">
                  Searching…
                </div>
              ) : results.length === 0 ? (
                <div className="px-3 py-2 text-sm text-text-muted">
                  No matches. Try another word or a code.
                </div>
              ) : (
                results.map((row, idx) => {
                  // Defensive unwrap — even though `/api/Master/AllergyCode`
                  // hasn't been reported as wrapped, every other master
                  // endpoint that ships Codings has surfaced the wrapped
                  // shape (DiagnosisCodes / ProcedureCodesList /
                  // VaccinationCodesList). Apply the same defensive
                  // pattern preemptively.
                  const inner = unwrapMasterRow(row);
                  if (!inner) return null;
                  const codeStr = readStringField(inner, 'code') ?? '';
                  const displayStr = (readStringField(inner, 'display') ?? '').trim();
                  const flat = {
                    ...(row.category != null ? { category: row.category } : {}),
                    code: codeStr || null,
                    display: displayStr || null,
                    system: readStringField(inner, 'system'),
                    description: readStringField(inner, 'description'),
                    shortName: readStringField(inner, 'shortName'),
                  };
                  const rowKey = `${codeStr}-${displayStr}-${idx}`;
                  return (
                    <button
                      key={rowKey}
                      type="button"
                      onClick={() => {
                        onChange(JSON.stringify(flat));
                        setQuery('');
                      }}
                      className="block w-full px-3 py-2 text-left hover:bg-grey-100/50"
                    >
                      <span className="text-sm text-text-primary block">
                        {displayStr || codeStr || '—'}
                      </span>
                      {row.category?.display ? (
                        <span className="text-xs text-text-hint">
                          {row.category.display}
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          ) : null}
        </>
      )}

      {error ? (
        <p className="text-xs text-red-700 mt-1" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

/**
 * Inline error UI shown beneath a master-data dropdown when its fetch
 * fails. Provides a manual Retry button — the global toast handler
 * already surfaces the failure separately, but the empty dropdown
 * without context-local feedback is poor UX on a write surface.
 * Samer PR #214 review 2026-05-19.
 */
function MasterDataError({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="alert"
      className="mt-1 flex items-center justify-between gap-2 text-xs text-red-700"
    >
      <span>Could not load options.</span>
      <button
        type="button"
        onClick={onRetry}
        className="font-semibold underline underline-offset-2 hover:text-red-900"
      >
        Retry
      </button>
    </div>
  );
}

function safeParse<T>(s: string): T | null {
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

/**
 * Normalize `performerDetails` to the strict `PerformerDetailsFull`
 * shape for the UPDATE wire body. The GET-side type is
 * `Partial<PerformerDetailsFull>` to give the parser headroom for the
 * server occasionally dropping a field, but the UPDATE wire body MUST
 * ship every field present-and-null rather than missing-via-undefined
 * (JSON.stringify drops `undefined`, which can change the request
 * payload silently). Samer PR #219 review §2.
 *
 * Returns null only when there's no performer record at all — that's
 * the legit "no performer" case (some migrated records).
 */
/**
 * Normalize a `CodeWordCase` to its strict 5-field shape (with explicit
 * nulls for missing fields). The master-data endpoints
 * (`/Master/AllergenReaction`, `/Master/AllergenSeverity`) return
 * 3-field Codings (`code` / `display` / `system`); the GET-side
 * `category` Coding can also come back 3-field. JSON.stringify drops
 * `undefined` keys, so without this we ship a 3-field wire body where
 * the spec curl shows 5 fields. Backend tolerates both but Flutter
 * parity is the 5-field shape (Samer PR #219 review + Andrew's
 * follow-up cleanup ask 2026-05-19).
 *
 * Returns `null` when the input is null/undefined so callers can stay
 * one-liner.
 */
export function normalizeCoding(
  c: Partial<CodeWordCase> | null | undefined,
): CodeWordCase | null {
  if (!c) return null;
  return {
    code: c.code ?? null,
    display: c.display ?? null,
    system: c.system ?? null,
    description: c.description ?? null,
    shortName: c.shortName ?? null,
  };
}

export function normalizePerformerDetails(
  pd: Partial<PerformerDetailsFull> | null | undefined,
): PerformerDetailsFull | null {
  if (!pd) return null;
  return {
    id: pd.id ?? null,
    name: pd.name ?? null,
    photo: pd.photo ?? null,
    speciality: pd.speciality
      ? {
          specialitySkillId: pd.speciality.specialitySkillId ?? null,
          code: pd.speciality.code
            ? {
                code: pd.speciality.code.code ?? null,
                displayName: pd.speciality.code.displayName ?? null,
                system: pd.speciality.code.system ?? null,
                description: pd.speciality.code.description ?? null,
                shortName: pd.speciality.code.shortName ?? null,
              }
            : null,
        }
      : null,
    timeZone: pd.timeZone ?? null,
    organizationId: pd.organizationId ?? null,
    roleName: pd.roleName ?? null,
  };
}


